import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import AppContext from '../../contexts/AppContext'
import { fetchRoleSystem } from '../../services/roleSystem.api'
import { parseBBCode } from '../../libraries/BBCode'
import { roleSystemBbCodeTags } from './roleSystemTags'
import RoleSystemDetailHero from './RoleSystemDetailHero'
import RoleSystemToc from './RoleSystemToc'
import { extractTocFromBBCode } from './roleSystemToc'
import '../../styles/parchment-page.css'
import './RoleSystems.css'

export default function ViewRoleSystem() {
  const { slug } = useParams()
  const { user } = useContext(AppContext)
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [forbidden, setForbidden] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      setForbidden(false)
      try {
        const row = await fetchRoleSystem(slug)
        if (!cancelled) {
          setItem(row)
          document.title = row?.title
            ? `Shinobis' War :: ${row.title}`
            : "Shinobis' War :: Sistema"
        }
      } catch (e) {
        if (!cancelled) {
          const status = e?.status ?? e?.response?.status
          if (status === 403) {
            setForbidden(true)
            setError(
              user?.userId
                ? 'Este volumen está sellado para tu rango. Si crees que deberías tener acceso, contacta con el staff.'
                : 'Este volumen requiere iniciar sesión. Regístrate o entra con tu cuenta para consultarlo.'
            )
          } else if (status === 404) {
            setError('Volumen no encontrado en el códice.')
          } else {
            setError(e?.message || 'Error cargando el sistema')
          }
          setItem(null)
          document.title = "Shinobis' War :: Sistemas"
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [slug, user?.userId])

  const tocItems = useMemo(
    () => (item?.content ? extractTocFromBBCode(item.content) : []),
    [item?.content]
  )

  const contentHtml = useMemo(
    () => (item?.content ? parseBBCode(item.content, roleSystemBbCodeTags) : ''),
    [item?.content]
  )

  return (
    <div className="page parchment-scope role-systems-page role-systems-page--detail">
      {loading && (
        <div className="role-systems-page__inner">
          <div className="role-systems-page__panel">
            <p className="role-systems-page__empty">Abriendo volumen…</p>
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="role-systems-page__inner">
          <Link to="/sistemas-de-rol" className="role-system-detail-hero__back role-system-detail-hero__back--solo">
            <span aria-hidden="true">←</span> Volver al códice
          </Link>
          <div className="role-systems-page__panel role-systems-page__panel--forbidden">
            <span className="role-systems-page__forbidden-kicker">
              {forbidden ? 'Volumen sellado' : 'No disponible'}
            </span>
            <div className={forbidden ? 'role-system-view__forbidden' : 'role-systems-page__error'}>
              {error}
            </div>
            {forbidden && !user?.userId ? (
              <p className="role-systems-page__forbidden-actions">
                <Link to="/entrar">Iniciar sesión</Link>
                <span aria-hidden="true"> · </span>
                <Link to="/registro">Registrarse</Link>
              </p>
            ) : null}
          </div>
        </div>
      )}

      {!loading && !error && item && (
        <>
          <RoleSystemDetailHero
            title={item.title}
            summary={item.summary}
            sortOrder={item.sortOrder}
            updatedAt={item.updatedAt}
          />

          <div className="role-systems-page__inner">
            <div
              className={`role-system-view__layout${
                tocItems.length ? ' role-system-view__layout--with-toc' : ''
              }`}
            >
              {tocItems.length > 0 ? <RoleSystemToc items={tocItems} /> : null}

              <article className="role-system-view__article">
                <div
                  className="role-system-view__content story-text story-bbcode role-system-view__body"
                  dangerouslySetInnerHTML={{ __html: contentHtml }}
                />
              </article>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
